import { Component, OnInit } from '@angular/core';
import { OperationError } from '../../../../../models/server-result/operation-error';
import { TranslateService } from '../../../../../services/translate/translate.service';
import { ServerApiService } from '../../../../../server-api/server-api.service';
import { LogService } from '../../../../../services/log/log.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ValidateFileContentByUploadIdRequest } from '../../../../../models/upload/validate-file-content-by-upload-id-request';
import { ValidateDocumentContentByUploadIdResult } from '../../../../../models/upload/validate-file-content-by-upload-id-result';
import { OperationResult } from '../../../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { ProcessFileContentByUploadIdRequest } from '../../../../../models/upload/process-file-content-by-upload-id-request';
import { ServerErrors } from '../../../../../models/enums/server-errors';
import { DocumentAmountCurrencyData } from '../../../../../models/upload/documents/document-amount-currency-data';
import { ScreenWidthListenerService } from '../../../../../services/screen-width-detector/screen-width-listener.service';
import {
	DocumentUploadContractType,
	DocumentUploadContractTypeExtensions,
} from '../../../../../models/enums/document-upload-contract-type';
import { DocumentRecordValidationViewModel } from '../view-models/document-record-validation-view-model';
import { DocumentsUploadInstructionsModalComponent } from '../instructions-modal/instructions-modal.component';
import { Step } from '../../../../../shared-components/custom-stepper/custom-stepper.component';
import { RouteAuthorizationService } from '../../../../../services/authorization/route-authorization.service';
import { AppContextService } from '../../../../../services/app-context.service';
import { User } from '../../../../../models/User/user';
import {
	UploadDocumentsCountCriteria,
	UploadDocumentsResult,
	UploadDocumentsSearchCriteria,
} from '../../../../../server-api/upload-documents-api/upload-documents-api.models';
import { UploadDocumentsApiService } from '../../../../../server-api/upload-documents-api/upload-documents-api.service';
import { ToasterService } from '../../../../../shared-components/custom-toaster/toaster.service';
import {
	TableFilter,
	TablePagination,
	FilterOption,
	TableSearchEvent,
} from '../../../../../shared-components/custom-table/custom-table.types';
import {
	CustomTableFilterMapperService,
	FilterMappingConfig,
} from '../../../../../shared-components/custom-table/custom-table-filter-mapper.service';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CustomModalService } from '../../../../../shared-components/custom-modal/custom-modal.service';
import { CodeListApiService } from '../../../../../server-api/code-list-api/code-list-api.service';
import { CodeList } from '../../../../../models/common/code-list';
import { UploadDocumentsValidationCodesEnum } from '../../../../../server-api/code-list-api/code-list-api.enums';

@Component({
	selector: 'app-documents-upload-step3',
	templateUrl: './documents-upload-step3.component.html',
	styleUrls: ['../documents-upload.scss'],
	standalone: false,
})
export class DocumentsUploadStep3Component implements OnInit {
	protected isPerformingRequest = false;
	public validationResultRecords: DocumentRecordValidationViewModel[] = [];
	protected uploadId: string;
	protected totalValidRecords: number = 0;
	protected totalInvalidRecords: number = 0;

	protected loadedRecordsCounts: Promise<boolean> = Promise.resolve(false);
	protected validSumAmountPerCurrency: DocumentAmountCurrencyData[] = [];
	protected invalidSumAmountPerCurrency: DocumentAmountCurrencyData[] = [];
	private uploadContractType: DocumentUploadContractType | null = null;
	private fileRelatedContractId: string;
	private loggedUser: User | null = new User();
	private validationResult: ValidateDocumentContentByUploadIdResult | null = null;

	public tablePagination: TablePagination = {
		pageIndex: 0,
		pageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
		totalItems: 0,
	};
	public tableFilters: TableFilter[] = [];
	public isTableLoading = true;

	protected filterMappings: FilterMappingConfig[] = [
		{
			field: 'validationStatusList',
			targetProperty: 'validationStatusList',
			type: 'array',
		},
		{
			field: 'uploadedDocumentType',
			targetProperty: 'uploadedDocumentType',
			type: 'string',
		},
		{
			field: 'uploadedDocumentNumber',
			targetProperty: 'uploadedDocumentNumber',
			type: 'string',
		},
		{
			field: 'uploadedDocumentSupplier',
			targetProperty: 'uploadedDocumentSupplier',
			type: 'string',
		},
		{
			field: 'uploadedDocumentBuyer',
			targetProperty: 'uploadedDocumentBuyer',
			type: 'string',
		},
	];

	protected searchCriteria: UploadDocumentsSearchCriteria = new UploadDocumentsSearchCriteria();

	currentStep: number = 2;
	steps: Step[] = [];

	constructor(
		private translateService: TranslateService,
		private serverApiService: ServerApiService,
		private codeListApiService: CodeListApiService,
		private logger: LogService,
		private router: Router,
		private appContext: AppContextService,
		private route: ActivatedRoute,
		private modalService: CustomModalService,
		protected screenWidthListener: ScreenWidthListenerService,
		private routeAuthorizationService: RouteAuthorizationService,
		private uploadDocumentsApiService: UploadDocumentsApiService,
		private toasterService: ToasterService,
		protected filterMapper: CustomTableFilterMapperService
	) {
		this.initializeSteps();
	}

	initializeSteps(): void {
		this.steps = [
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step1Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step2Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step3Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('documents-upload-wizard-steps.Step4Label', 'client'),
				icon: 'fa-solid fa-check',
			},
		];
	}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			if (this.canAccessStep3()) {
				this.route.params.subscribe((params) => {
					const uploadContractTypeRouteParam = params['uploadContractType'] || '';
					if (!!uploadContractTypeRouteParam) {
						this.uploadContractType = DocumentUploadContractTypeExtensions.FromRouteParam(uploadContractTypeRouteParam);
					}

					this.uploadId = params['uploadId'] || '';
					this.fileRelatedContractId = params['contractId'] || '';

					if (!this.uploadId || !this.uploadContractType || !this.fileRelatedContractId) {
						this.logger.error(
							'Missing params on documents upload step3.',
							this.uploadId,
							this.uploadContractType,
							this.fileRelatedContractId
						);
						this.toasterService.showError(
							this.translateService.translate('documents-upload-step3.missing-params-on-documents-upload-step3', 'client')
						);
						return;
					} else {
						this.initializeTableFilters();
						this.loadValidationStatusOptions();
						this.loadData();
					}
				});
			} else {
				this.routeAuthorizationService.routeOnUnauthorized();
			}
		});
	}

	private loadData(): void {
		const validateFileContentByUploadIdRequest: ValidateFileContentByUploadIdRequest = <ValidateFileContentByUploadIdRequest>{
			UploadId: this.uploadId,
			DocumentUploadContractType: this.uploadContractType,
		};

		this.isPerformingRequest = true;
		this.serverApiService.validateDocumentRecordsExcelFileContent(validateFileContentByUploadIdRequest).subscribe({
			next: (operationResult: OperationResult<ValidateDocumentContentByUploadIdResult>) => {
				this.isPerformingRequest = false;
				if (operationResult.IsValid) {
					this.validationResult = operationResult.Data;
					this.processExceededValidationLimit(operationResult);
					this.loadUploadedDocuments();
				} else {
					this.processInValidValidateDocumentContentByUploadIdResult(operationResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isPerformingRequest = false;
				this.isTableLoading = false;
				this.validationResultRecords = [];
				this.logger.error('[DocumentsUploadStep3Component::ValidateFileContentByUploadIdRequest] HttpErrorResponse: ', httpErrorResponse);
				this.toasterService.showError(
					'[DocumentsUploadStep3Component::ValidateFileContentByUploadIdRequest] HttpErrorResponse: ' +
						httpErrorResponse.error +
						httpErrorResponse.message
				);
			},
		});
	}

	private processExceededValidationLimit(operationResult: OperationResult<ValidateDocumentContentByUploadIdResult>): void {
		const warning = operationResult.Warnings?.find((error) => error.Code === 'EXCEEDED_MAX_NUMBER_OF_ALLOWED_RECORDS_TO_VALIDATE');
		if (warning) {
			this.toasterService.showWarning(this.translateService.translate(warning.Code, 'list-errorCodes'));
		}
	}

	private loadUploadedDocuments(): void {
		try {
			this.extractCountsAndAmountsFromValidationResult();
		} catch (error) {
			this.logger.error('[DocumentsUploadStep3Component::extractCountsAndAmountsFromValidationResult] Error: ', error);
			this.totalValidRecords = 0;
			this.totalInvalidRecords = 0;
			this.validSumAmountPerCurrency = [];
			this.invalidSumAmountPerCurrency = [];
			this.loadedRecordsCounts = Promise.resolve(true);
		}
		this.performSearch(0);
	}

	protected performSearch(pageNumber: number): void {
		if (!this.uploadId) {
			this.logger.error('[DocumentsUploadStep3Component::performSearch] uploadId is not set');
			this.isTableLoading = false;
			return;
		}

		this.isTableLoading = true;

		const uploadDocumentsSearchCriteria = new UploadDocumentsSearchCriteria();
		uploadDocumentsSearchCriteria.OrderBy = 'RowNumber';
		uploadDocumentsSearchCriteria.OrderDescending = false;
		uploadDocumentsSearchCriteria.PageNumber = pageNumber;
		uploadDocumentsSearchCriteria.RowsPerPage = this.tablePagination.pageSize;
		uploadDocumentsSearchCriteria.RestrictionCriteria = this.buildRestrictionCriteria();

		const countCriteria: UploadDocumentsCountCriteria = this.buildRestrictionCriteria();

		forkJoin({
			search: this.uploadDocumentsApiService.uploadDocumentsSearch(uploadDocumentsSearchCriteria),
			count: this.uploadDocumentsApiService.uploadDocumentsCount(countCriteria),
		})
			.pipe(
				finalize(() => {
					this.isTableLoading = false;
				})
			)
			.subscribe({
				next: ({ search, count }) => {
					if (search.IsValid) {
						this.validationResultRecords = (search.Data || []).map((record) => this.toValidationViewModel(record));
					} else {
						this.validationResultRecords = [];
						this.toasterService.showError('Error searching documents', {
							errors: search.Errors,
							autoClose: false,
						});
					}

					if (count.IsValid) {
						this.tablePagination.totalItems = count.Data ?? 0;
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					this.logger.error('[DocumentsUploadStep3Component::performSearch] HttpErrorResponse: ', httpErrorResponse);
					this.validationResultRecords = [];
					this.toasterService.showError(httpErrorResponse.message);
				},
			});
	}

	private extractCountsAndAmountsFromValidationResult(): void {
		if (!this.validationResult) {
			return;
		}

		this.totalValidRecords = this.validationResult.TotalValidRecords ?? 0;
		this.totalInvalidRecords = this.validationResult.TotalInvalidRecords ?? 0;
		this.validSumAmountPerCurrency = this.convertMapToCurrencyData(this.validationResult.ValidRecordsNetAmountsPerCurrency);
		this.invalidSumAmountPerCurrency = this.convertMapToCurrencyData(this.validationResult.InvalidRecordsNetAmountsPerCurrency);
		this.loadedRecordsCounts = Promise.resolve(true);
	}

	private convertMapToCurrencyData(map: Map<string, number> | Record<string, number> | null | undefined): DocumentAmountCurrencyData[] {
		if (!map) {
			return [];
		}
		if (map instanceof Map) {
			return Array.from(map.entries()).map(([currency, amount]) => ({
				Currency: currency,
				SumAmount: amount,
			}));
		} else {
			return Object.entries(map).map(([currency, amount]) => ({
				Currency: currency,
				SumAmount: amount,
			}));
		}
	}

	private processInValidValidateDocumentContentByUploadIdResult(
		operationResult: OperationResult<ValidateDocumentContentByUploadIdResult>
	): void {
		let errors = this.changeErrorCodes(operationResult.Errors);
		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}

		this.isTableLoading = false;
		this.validationResultRecords = [];
		this.totalInvalidRecords = 0;
		this.loadedRecordsCounts = Promise.resolve(true);
	}

	protected redirectToDocumentsStep2(): void {
		this.router.navigate([
			'/private/files-upload/upload/documents/step2',
			DocumentUploadContractTypeExtensions.ToRouteParam(this.uploadContractType!),
			this.fileRelatedContractId,
		]);
	}

	protected goToResumeStep(): void {
		this.router.navigate([
			'/private/files-upload/upload/documents/step4',
			this.uploadId,
			DocumentUploadContractTypeExtensions.ToRouteParam(this.uploadContractType!),
			this.fileRelatedContractId,
		]);
	}

	protected submitRecords() {
		if (this.totalValidRecords === 0) {
			this.processUploadedRecords();
		} else {
			const modalRef = this.modalService.confirm({
				size: 'lg',
				title: this.translateService.translate('document-upload-step3.modalConfirmDialogConfirmSubmissionTitle', 'client'),
				body: {
					content: this.translateService
						.translate('document-upload-step3.modalConfirmDialogConfirmSubmissionContent', 'client')
						.replace('[numberOfValidDocs]', this.totalValidRecords.toString())
						.replace('[totalNumberOfDocs]', (this.totalValidRecords + this.totalInvalidRecords).toString()),
				},
			});

			modalRef.result
				.then((confirmed) => {
					if (confirmed) {
						this.processUploadedRecords();
					}
				})
				.catch(() => undefined);
		}
	}

	private processUploadedRecords(): void {
		const processFileContentByUploadIdRequest: ProcessFileContentByUploadIdRequest = <ProcessFileContentByUploadIdRequest>{
			UploadId: this.uploadId,
			DocumentUploadContractType: this.uploadContractType,
		};

		this.isPerformingRequest = true;
		this.serverApiService.processDocumentRecordsExcelFileContentByUploadId(processFileContentByUploadIdRequest).subscribe({
			next: (operationResult: OperationResult<boolean>) => {
				this.isPerformingRequest = false;
				if (operationResult.IsValid) {
					this.goToResumeStep();
				} else {
					this.processInvalidProcessFileContentByUploadIdResult(operationResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isPerformingRequest = false;
				this.logger.error('[DocumentsUploadStep3Component::ValidateFileContentByUploadIdRequest] HttpErrorResponse: ', httpErrorResponse);
				this.toasterService.showError(
					'[DocumentsUploadStep3Component::ValidateFileContentByUploadIdRequest] HttpErrorResponse: ' +
						httpErrorResponse.error +
						httpErrorResponse.message
				);
			},
		});
	}

	private processInvalidProcessFileContentByUploadIdResult(operationResult: OperationResult<boolean>): void {
		let errors = this.changeErrorCodes(operationResult.Errors);
		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}
	}

	private changeErrorCodes(errors: OperationError[]): OperationError[] {
		if (errors?.length > 0) {
			for (let index = 0; index < errors.length; index++) {
				if (errors[index].Code === ServerErrors.InternalError) {
					errors[index].Code = this.translateService.translate('document-upload-step3.' + ServerErrors.InternalError);
				}
			}
		}

		return errors ?? [];
	}

	protected showValidationResultsForConfirming(): boolean {
		return this.uploadContractType === DocumentUploadContractType.Confirming;
	}

	protected showValidationResultsForFactoring(): boolean {
		return this.uploadContractType === DocumentUploadContractType.Factoring;
	}

	protected translateValidationError(validationError: OperationError): string {
		if (validationError.Code === ServerErrors.ConnectionNotFoundForCurrency) {
			return validationError.Values[0];
		}
		return this.translateService.translate(validationError.Code, 'list-errorCodes');
	}

	protected hasEnteredRequiredData(): boolean {
		return !!this.uploadContractType && !!this.fileRelatedContractId && this.tablePagination.totalItems > 0;
	}

	protected openInstructionsModal(): void {
		const instructionsModalRef = this.modalService.open<string, { uploadContractType: DocumentUploadContractType | null }>({
			size: 'lg',
			title: this.translateService.translate('documents-upload-instructions-modal.title', 'client'),
			body: {
				kind: 'component',
				componentType: DocumentsUploadInstructionsModalComponent,
			},
			actions: [
				{
					id: 'start',
					label: this.translateService.translate('button.start', 'client') || 'Start',
					color: 'primary',
					variant: 'outlined',
					autoFocus: true,
					run: ({ bodyComponent, modalRef }) => {
						const result = bodyComponent?.start();
						modalRef.close(result);
					},
				},
			],
			data: {
				uploadContractType: this.uploadContractType,
			},
		});

		instructionsModalRef.result.catch(() => undefined);
	}

	private canAccessStep3(): boolean {
		return (
			!!this.loggedUser &&
			!this.loggedUser.IsBackOfficeOperator &&
			(this.loggedUser.canUploadDocumentsForConfirming() || this.loggedUser.canUploadDocumentsForFactoring())
		);
	}

	protected onSearchRequested(event: TableSearchEvent): void {
		// Don't perform search if validation is still in progress
		if (this.isPerformingRequest) {
			return;
		}

		this.searchCriteria = new UploadDocumentsSearchCriteria();
		this.searchCriteria.OrderBy = 'RowNumber';
		this.searchCriteria.OrderDescending = false;
		this.searchCriteria.fileUploadId = this.uploadId;

		if (this.filterMapper && event.filters?.length > 0) {
			this.filterMapper.mapFiltersToObject(event.filters, this.filterMappings, this.searchCriteria);
		}

		this.tablePagination.pageIndex = event.pageIndex;
		this.tablePagination.pageSize = event.pageSize;

		this.performSearch(event.pageIndex);
	}

	protected shouldShowPagination(): boolean {
		return this.tablePagination.totalItems > this.tablePagination.pageSize;
	}

	private initializeTableFilters(): void {
		this.tableFilters = [
			{
				field: 'uploadedDocumentType',
				type: 'text',
				value: null,
				placeholder: this.translateService.translate('documents-upload-confirming-validation-result.DocumentRecordsDocType', 'client'),
			},
			{
				field: 'uploadedDocumentNumber',
				type: 'text',
				value: null,
				placeholder: this.translateService.translate('documents-upload-confirming-validation-result.DocumentRecordsNumber', 'client'),
			},
			{
				field: 'uploadedDocumentBuyer',
				type: 'text',
				value: null,
				placeholder: this.translateService.translate('documents-upload-confirming-validation-result.DocumentRecordsBuyerVat', 'client'),
			},
			{
				field: 'uploadedDocumentSupplier',
				type: 'text',
				value: null,
				placeholder: this.translateService.translate('documents-upload-confirming-validation-result.DocumentRecordsSupplierVat', 'client'),
			},
			{
				field: 'validationStatusList',
				type: 'select',
				value: null,
				options: this.getValidationStatusFilterOptions(),
				placeholder: this.translateService.translate('documents-upload-confirming-validation-result.ValidationStatus', 'client'),
			},
		];
	}

	private loadValidationStatusOptions(): void {
		this.codeListApiService.getCodeListUploadDocumentsValidationCodes().subscribe({
			next: (operationResult: OperationResult<CodeList<UploadDocumentsValidationCodesEnum>>) => {
				if (operationResult.IsValid) {
					const options = (operationResult.Data?.Codes || []).map((status) => ({
						value: status,
						label: this.translateService.translate(status, 'list-uploadedDocumentRecordState'),
					}));
					this.updateValidationStatusFilterOptions(options);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.toasterService.showError(httpErrorResponse.message);
			},
		});
	}

	private updateValidationStatusFilterOptions(options: FilterOption[]): void {
		this.tableFilters = this.tableFilters.map((filter) => {
			if (filter.field !== 'validationStatusList') {
				return filter;
			}
			return { ...filter, options };
		});
	}

	private getValidationStatusFilterOptions(): FilterOption[] {
		return this.tableFilters.find((filter) => filter.field === 'validationStatusList')?.options ?? [];
	}

	private toValidationViewModel(record: UploadDocumentsResult): DocumentRecordValidationViewModel {
		const validationData = record as DocumentRecordValidationViewModel;
		validationData.ShowExtendedData = false;
		if (
			validationData.ValidationErrors?.some((validationErrors) => validationErrors.Code === ServerErrors.DocumentNumberMaxStringLenError)
		) {
			validationData.ValidationErrors = validationData.ValidationErrors.map((validationErrors) => {
				if (validationErrors.Code === ServerErrors.DocumentNumberMaxStringLenError) {
					return { ...validationErrors, Code: validationErrors.Values[0] };
				}
				return validationErrors;
			});
		}
		return validationData;
	}

	protected buildRestrictionCriteria(): UploadDocumentsCountCriteria {
		return {
			fileUploadId: this.uploadId,
			validationStatusList: this.searchCriteria.validationStatusList,
			uploadedDocumentType: this.searchCriteria.uploadedDocumentType,
			uploadedDocumentNumber: this.searchCriteria.uploadedDocumentNumber,
			uploadedDocumentSupplier: this.searchCriteria.uploadedDocumentSupplier,
			uploadedDocumentBuyer: this.searchCriteria.uploadedDocumentBuyer,
		};
	}
}
